import { useQueries, useQuery } from "@tanstack/react-query";
import React, { useMemo, useState } from "react";
import { Alert, Button, Form, Modal, ProgressBar } from "react-bootstrap";
import { events } from "../../data/events";
import { CompanyType } from "../../models/company";
import { fetchCompanies } from "../../utils/http/companies";
import {
  fetchMailTemplates,
  fetchMailTemplateSends,
  fetchMailTemplateSummary,
} from "../../utils/http/mail-templates";
import {
  countIfs,
  deriveCell,
  deriveLivePaths,
  flattenEventPaths,
  MATRIX_LANGUAGE,
  MatrixPath,
  MatrixRow,
  pathKey,
  pLimit,
  dateNDaysAgo,
  sendsPathKey,
  wasSentSince,
} from "../../utils/mailTemplateMatrix";

// Shared, module-level concurrency limiter: every colibry-services call the page
// makes (summaries + per-path content) goes through this single instance, so the
// total number of in-flight requests stays bounded regardless of company/path count.
const limit = pLimit(8);

const GREEN = "#28a745";
const TEAL = "#17a2b8";
const BLUE = "#0d6efd";
const GREY = "#d5d5d5";

// Fixed widths for the two sticky left columns (used for both sizing and the
// sticky `left` offset of the second column).
const PATH_W = 320;
const SUM_W = 88;

// Internal / test customers to keep out of the matrix (matched case-insensitively
// against both the slug and the display name). "colibry" also covers "co-libry".
const EXCLUDED_COMPANIES = ["co-libry", "colibry", "fretest", "template", "testcustomer"];

interface ColumnData {
  summary: { maintained: any[]; config: any[] };
  counts: Map<string, number>;
  // pathKeys sent at least once in the last RECENT_SEND_DAYS.
  sentRecently: Set<string>;
}

// Rolling window for the "recently sent" marker.
const RECENT_SEND_DAYS = 31;

// Fetch a single company's summary + sends, then the content of only its live nl
// paths to count {% if %} structures. Per-path failures degrade to NaN ("?").
async function fetchColumnData(legacyId: string, allPaths: MatrixPath[], signal: any): Promise<ColumnData> {
  const summary = await limit(() => fetchMailTemplateSummary({ signal, legacyId }));

  // Sends drive the "sent last month" marker; a failure here shouldn't sink the
  // whole column, so fall back to an empty map.
  const sends: Record<string, Record<string, number>> = await limit(() =>
    fetchMailTemplateSends({ signal, legacyId }).catch(() => ({})),
  );
  const cutoff = dateNDaysAgo(new Date(), RECENT_SEND_DAYS);
  const sentRecently = new Set<string>();
  allPaths.forEach((p) => {
    if (wasSentSince(sends?.[sendsPathKey(p)], cutoff)) sentRecently.add(pathKey(p));
  });

  const livePaths = deriveLivePaths(summary, allPaths);
  const counts = new Map<string, number>();
  await Promise.all(
    livePaths.map((p) =>
      limit(async () => {
        try {
          const tpls = await fetchMailTemplates({
            signal,
            legacyId,
            mailTemplatePath: { ...p, language: MATRIX_LANGUAGE, version: "live" },
          });
          const live = tpls.find((t: any) => t.version === "live") ?? tpls[0];
          counts.set(pathKey(p), countIfs(live?.subject, live?.html));
        } catch {
          counts.set(pathKey(p), NaN);
        }
      }),
    ),
  );
  return { summary, counts, sentRecently };
}

// Deep link to the v3 mail-template editor for a specific company + path. The
// `?customer=` param is picked up by applyCustomerFromQuery to switch tenant.
function templateUrl(company: CompanyType, p: MatrixPath): string {
  const customer = company.name ?? company.id ?? "";
  return `/mail-templates/${MATRIX_LANGUAGE}/${p.automation}/${p.event}/${p.action}/${p.target}/live?customer=${encodeURIComponent(
    customer,
  )}`;
}

const Box = ({ on, color = GREEN }: { on: boolean; color?: string }) => (
  <span style={{ width: 12, height: 12, display: "inline-block", borderRadius: 2, background: on ? color : GREY }} />
);

type CellStatus = "loading" | "error" | "nolegacy" | "ready";

const Cell = React.memo(function Cell({
  status,
  sentRecently,
  active,
  liveExists,
  count,
  tooltip,
}: {
  status: CellStatus;
  sentRecently: boolean;
  active: boolean;
  liveExists: boolean;
  count: number | undefined;
  tooltip: string;
}) {
  if (status === "loading")
    return (
      <span className="text-muted" style={{ fontSize: "0.7rem" }}>
        …
      </span>
    );
  if (status === "error")
    return (
      <span className="text-danger" title={tooltip} style={{ fontSize: "0.7rem" }}>
        err
      </span>
    );
  if (status === "nolegacy")
    return (
      <span className="text-muted" title={tooltip} style={{ fontSize: "0.7rem" }}>
        —
      </span>
    );

  let countLabel: string;
  if (!liveExists) countLabel = "-";
  else if (count === undefined || Number.isNaN(count)) countLabel = "?";
  else countLabel = String(count);

  return (
    <div style={{ display: "flex", gap: 2, alignItems: "center", justifyContent: "center" }} title={tooltip}>
      <Box on={sentRecently} color={BLUE} />
      <Box on={active} />
      <Box on={liveExists} />
      <span style={{ minWidth: 16, fontSize: "0.7rem", textAlign: "center" }}>{countLabel}</span>
    </div>
  );
});

function MailTemplateMatrix() {
  const rows: MatrixRow[] = useMemo(() => flattenEventPaths(events), []);
  const allPaths: MatrixPath[] = useMemo(
    () => rows.filter((r): r is Extract<MatrixRow, { kind: "path" }> => r.kind === "path").map((r) => r.path),
    [rows],
  );

  // "tree" = grouped/collapsible by automation+event. The metric sorts drop the
  // tree and show a flat list ordered descending on the chosen roll-up.
  const [sort, setSort] = useState<"tree" | "sent" | "active" | "live" | "ifs">("tree");

  // Expanded by default (groups can be collapsed individually via the carets).
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Columns the user has hidden. Their queries keep running/caching, so hiding
  // and re-showing a company never refetches; totals recompute over what's shown.
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const hideCompany = (key: string) => setHidden((prev) => new Set(prev).add(key));
  const showCompany = (key: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });

  // Quick Entry: edit the whole hidden set at once as a comma-separated list.
  const [showQuickEntry, setShowQuickEntry] = useState(false);
  const [quickEntryText, setQuickEntryText] = useState("");

  // X axis: all active + onboarding companies (paginated), minus internal/test
  // customers, sorted by display name.
  const {
    data: companies = [],
    isLoading: companiesLoading,
    error: companiesError,
  } = useQuery({
    queryKey: ["mtmatrix", "companies"],
    staleTime: 5 * 60 * 1000,
    queryFn: async ({ signal }) => {
      const all: CompanyType[] = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const data = await fetchCompanies({ status: "active_onboarding", page, size: 50, signal });
        all.push(...(data.items ?? []));
        hasMore = !!data.items?.length && all.length < (data.total ?? all.length);
        page++;
      }
      return all
        .filter((c) => {
          const haystack = `${c.name ?? ""} ${c.info?.name ?? ""}`.toLowerCase();
          return !EXCLUDED_COMPANIES.some((x) => haystack.includes(x));
        })
        .sort((a, b) => (a.info?.name || a.name).localeCompare(b.info?.name || b.name));
    },
  });

  // One query per company so columns stream in independently and one failure
  // never blanks the grid.
  const columnQueries = useQueries({
    queries: companies.map((company) => {
      const legacyId = company.legacy_data?.id as string | undefined;
      return {
        queryKey: ["mtmatrix", "company", legacyId ?? company.id ?? company.name],
        enabled: !!legacyId,
        staleTime: 5 * 60 * 1000,
        retry: 1,
        queryFn: ({ signal }: { signal: any }) => fetchColumnData(legacyId as string, allPaths, signal),
      };
    }),
  });

  const companyKey = (c: CompanyType) => (c.id ?? c.name) as string;

  // Pair each company with its query, then split by the hidden set. Hidden
  // companies keep their (cached) query alive so re-showing is instant.
  const columns = companies.map((company, i) => ({ company, query: columnQueries[i], key: companyKey(company) }));
  const visibleColumns = columns.filter((col) => !hidden.has(col.key));
  const hiddenColumns = columns.filter((col) => hidden.has(col.key));
  const visibleCount = visibleColumns.length;

  const openQuickEntry = () => {
    setQuickEntryText(hiddenColumns.map(({ company: c }) => c.info?.name || c.name).join(", "));
    setShowQuickEntry(true);
  };
  const applyQuickEntry = () => {
    const tokens = quickEntryText
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
    const next = new Set<string>();
    tokens.forEach((tok) => {
      const match = companies.find(
        (c) =>
          companyKey(c).toLowerCase() === tok ||
          (c.name ?? "").toLowerCase() === tok ||
          (c.info?.name ?? "").toLowerCase() === tok,
      );
      if (match) next.add(companyKey(match));
    });
    setHidden(next);
    setShowQuickEntry(false);
  };

  // Companies without a legacy id never run a query, so treat them as "done" to
  // keep the progress bar from hanging below 100%.
  const total = companies.length;
  const done = columns.filter(
    (col) => !col.company.legacy_data?.id || col.query?.isSuccess || col.query?.isError,
  ).length;

  const cellStatus = (company: CompanyType, query: (typeof columns)[number]["query"]): CellStatus => {
    if (!company.legacy_data?.id) return "nolegacy";
    if (query?.isError) return "error";
    if (query?.isSuccess) return "ready";
    return "loading";
  };

  // All visible columns have reached a terminal state (used to defer the red
  // "unused" flag until the roll-up over the shown companies is final).
  const allLoaded =
    visibleColumns.length > 0 && visibleColumns.every((col) => cellStatus(col.company, col.query) !== "loading");

  // Per-path roll-up over the VISIBLE companies, derived from already-fetched
  // in-memory data (changing the sort or hiding a column never refetches).
  const pathAgg = (p: MatrixPath) => {
    const perCompany = visibleColumns.map(({ company: c, query }) => {
      const status = cellStatus(c, query);
      const data = query?.data as ColumnData | undefined;
      const derived =
        status === "ready" && data ? deriveCell(data.summary, p) : { active: false, liveExists: false };
      const count = data?.counts.get(pathKey(p));
      const sent = status === "ready" && data ? data.sentRecently.has(pathKey(p)) : false;
      return { c, status, derived, count, sent };
    });
    const nSent = perCompany.filter((x) => x.status === "ready" && x.sent).length;
    const nActive = perCompany.filter((x) => x.status === "ready" && x.derived.active).length;
    const nLive = perCompany.filter((x) => x.status === "ready" && x.derived.liveExists).length;
    const nIf = perCompany.filter(
      (x) => x.status === "ready" && typeof x.count === "number" && !Number.isNaN(x.count) && x.count > 0,
    ).length;
    // Neither active nor live for any visible company (dead path). Only flagged
    // once everything has loaded so it doesn't flash red mid-load.
    const unused = allLoaded && nActive === 0 && nLive === 0;
    return { perCompany, nSent, nActive, nLive, nIf, unused };
  };

  type PathAgg = ReturnType<typeof pathAgg>;

  const renderPathRow = (p: MatrixPath, key: string, label: React.ReactNode, padLeft: number, agg: PathAgg) => {
    const { perCompany, nSent, nActive, nLive, nIf, unused } = agg;
    return (
      <tr key={key}>
        <td
          style={{
            position: "sticky",
            left: 0,
            zIndex: 1,
            background: "#fff",
            width: PATH_W,
            minWidth: PATH_W,
            maxWidth: PATH_W,
            padding: `2px 8px 2px ${padLeft}px`,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            borderRight: "1px solid #dee2e6",
            borderBottom: "1px solid #f1f1f1",
          }}
          title={`${p.automation} / ${p.event} / ${p.action} / ${p.target}`}
        >
          {label}
        </td>
        <td
          style={{
            position: "sticky",
            left: PATH_W,
            zIndex: 1,
            background: unused ? "#f8d7da" : "#fafbfc",
            width: SUM_W,
            minWidth: SUM_W,
            maxWidth: SUM_W,
            textAlign: "center",
            padding: "2px 4px",
            fontSize: "0.72rem",
            borderRight: "2px solid #ced4da",
            borderBottom: "1px solid #f1f1f1",
          }}
          title={`${nSent} sent last month · ${nActive} active · ${nLive} live · ${nIf} with {% if %} (of ${visibleCount} shown companies)`}
        >
          <span style={{ color: BLUE }}>{nSent}</span>
          <span className="text-muted">·</span>
          <span style={{ color: GREEN }}>{nActive}</span>
          <span className="text-muted">·</span>
          <span style={{ color: TEAL }}>{nLive}</span>
          <span className="text-muted">·</span>
          <span style={{ color: "#495057" }}>{nIf}</span>
        </td>
        {perCompany.map(({ c, status, derived, count, sent }) => (
          <td
            key={c.id ?? c.name}
            onClick={() => window.open(templateUrl(c, p), "_blank", "noopener")}
            style={{
              textAlign: "center",
              padding: "2px 2px",
              cursor: "pointer",
              borderRight: "1px solid #f1f1f1",
              borderBottom: "1px solid #f1f1f1",
            }}
          >
            <Cell
              status={status}
              sentRecently={sent}
              active={derived.active}
              liveExists={derived.liveExists}
              count={count}
              tooltip={`${c.info?.name || c.name} — ${p.event}/${p.action}/${p.target}${sent ? " · sent in last 31 days" : ""} (click to open)`}
            />
          </td>
        ))}
      </tr>
    );
  };

  // Flat, descending-sorted rows for the metric sorts (computed from in-memory
  // aggregates; secondary key keeps a stable order across equal values).
  const sortedRows =
    sort === "tree"
      ? []
      : allPaths
          .map((p, i) => ({ p, i, agg: pathAgg(p) }))
          .sort((a, b) => {
            const key =
              sort === "sent"
                ? "nSent"
                : sort === "active"
                  ? "nActive"
                  : sort === "live"
                    ? "nLive"
                    : "nIf";
            return b.agg[key] - a.agg[key] || a.i - b.i;
          });

  return (
    <div>
      <div className="mb-2">
        <h3 className="mb-1">Mail-Template Coverage Matrix</h3>
        <div className="text-muted" style={{ fontSize: "0.85rem" }}>
          Per company (active + onboarding) and template path: <Box on={true} color={BLUE} /> sent in last 31
          days&nbsp;·&nbsp;<Box on={true} /> active&nbsp;·&nbsp;
          <Box on={true} /> live version exists&nbsp;·&nbsp;<strong>N</strong> = number of distinct variables driving{" "}
          <code>{"{% if %}"}</code> conditionals in the live subject + body (a variable used in multiple IFs counts
          once; boilerplate such as <code>LANGUAGE</code> is excluded). Live/if-count reflect the{" "}
          <code>{MATRIX_LANGUAGE}</code> language only.
        </div>
      </div>
      <hr className="mb-3" />

      <div className="mb-3 d-flex align-items-center gap-2">
        <span className="text-muted" style={{ fontSize: "0.85rem" }}>
          Sort:
        </span>
        <Form.Select
          size="sm"
          style={{ width: "auto" }}
          value={sort}
          onChange={(e) => setSort(e.target.value as typeof sort)}
        >
          <option value="tree">Tree (automation / event)</option>
          <option value="sent">Most sent (last month)</option>
          <option value="active">Most active</option>
          <option value="live">Most live</option>
          <option value="ifs">Most IFs</option>
        </Form.Select>
        {sort !== "tree" && (
          <span className="text-muted" style={{ fontSize: "0.8rem" }}>
            Flat list, sorted descending — grouping disabled.
          </span>
        )}
        <Button variant="outline-secondary" size="sm" className="ms-auto" onClick={openQuickEntry}>
          <i className="bi bi-input-cursor-text me-1" />
          Quick entry
        </Button>
      </div>

      <Modal show={showQuickEntry} onHide={() => setShowQuickEntry(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Quick entry — hidden companies</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted" style={{ fontSize: "0.85rem" }}>
            Comma-separated list of companies to hide. Edit freely — matched case-insensitively on name or slug;
            unrecognised entries are ignored. Applying replaces the whole hidden set (empty = show all).
          </p>
          <Form.Control
            as="textarea"
            rows={4}
            value={quickEntryText}
            onChange={(e) => setQuickEntryText(e.target.value)}
            placeholder="e.g. Acme, another-company, ..."
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowQuickEntry(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={applyQuickEntry}>
            Apply
          </Button>
        </Modal.Footer>
      </Modal>

      {hiddenColumns.length > 0 && (
        <div className="mb-3 d-flex align-items-center flex-wrap gap-2">
          <span className="text-muted" style={{ fontSize: "0.85rem" }}>
            Hidden ({hiddenColumns.length}):
          </span>
          {hiddenColumns.map(({ company: c, key }) => (
            <span
              key={key}
              className="d-inline-flex align-items-center border rounded bg-light"
              style={{ fontSize: "0.8rem", paddingLeft: 8 }}
            >
              {c.info?.name || c.name}
              <button
                type="button"
                onClick={() => showCompany(key)}
                title="Add back"
                className="btn btn-sm btn-link p-0 px-1 text-decoration-none"
                style={{ lineHeight: 1 }}
              >
                <i className="bi bi-plus-lg" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={() => setHidden(new Set())}
            className="btn btn-sm btn-link p-0 text-decoration-none"
            style={{ fontSize: "0.8rem" }}
          >
            Show all
          </button>
        </div>
      )}

      {companiesError ? (
        <Alert variant="danger">{(companiesError as any)?.message || "Failed to load companies."}</Alert>
      ) : null}

      {total > 0 && done < total ? (
        <ProgressBar className="mb-3" now={done} max={total} label={`Loading companies ${done}/${total}`} />
      ) : null}

      {companiesLoading ? (
        <div className="text-muted">Loading companies…</div>
      ) : (
        <div style={{ maxHeight: "calc(100vh - 220px)", overflow: "auto", border: "1px solid #dee2e6" }}>
          <table style={{ borderCollapse: "separate", borderSpacing: 0, fontSize: "0.8rem" }}>
            <thead>
              <tr>
                <th
                  style={{
                    position: "sticky",
                    left: 0,
                    top: 0,
                    zIndex: 3,
                    background: "#f1f3f5",
                    width: PATH_W,
                    minWidth: PATH_W,
                    maxWidth: PATH_W,
                    textAlign: "left",
                    padding: "4px 8px",
                    borderRight: "1px solid #dee2e6",
                    borderBottom: "1px solid #dee2e6",
                  }}
                >
                  Path
                </th>
                <th
                  title="Across shown companies: # sent last month · # active · # with a live version · # with at least one {% if %}"
                  style={{
                    position: "sticky",
                    left: PATH_W,
                    top: 0,
                    zIndex: 3,
                    background: "#f1f3f5",
                    width: SUM_W,
                    minWidth: SUM_W,
                    maxWidth: SUM_W,
                    textAlign: "center",
                    padding: "4px 4px",
                    fontSize: "0.7rem",
                    borderRight: "2px solid #ced4da",
                    borderBottom: "1px solid #dee2e6",
                  }}
                >
                  <span style={{ color: BLUE }}>S</span> · <span style={{ color: GREEN }}>A</span> ·{" "}
                  <span style={{ color: TEAL }}>L</span> · <span style={{ color: "#495057" }}>IF</span>
                </th>
                {visibleColumns.map(({ company: c, query, key }) => (
                  <th
                    key={key}
                    title={`${c.info?.name || c.name}${c.legacy_data?.id ? "" : " (no legacy id)"}`}
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      background: "#f1f3f5",
                      height: 160,
                      minWidth: 46,
                      maxWidth: 46,
                      padding: "6px 2px",
                      verticalAlign: "bottom",
                      borderRight: "1px solid #eee",
                      borderBottom: "1px solid #dee2e6",
                    }}
                  >
                    <span
                      onClick={() => hideCompany(key)}
                      title="Remove this company"
                      style={{
                        position: "absolute",
                        top: 2,
                        left: 0,
                        right: 0,
                        textAlign: "center",
                        cursor: "pointer",
                        color: "#adb5bd",
                        fontSize: "0.9rem",
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </span>
                    <div
                      style={{
                        writingMode: "vertical-rl",
                        transform: "rotate(180deg)",
                        whiteSpace: "nowrap",
                        maxHeight: 138,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        margin: "0 auto",
                        fontWeight: 500,
                      }}
                    >
                      {query?.isLoading && c.legacy_data?.id ? <span className="text-muted">⏳ </span> : null}
                      {c.info?.name || c.name}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sort === "tree"
                ? rows.map((row) => {
                    if (row.kind === "automation") {
                      const isCollapsed = collapsed.has(row.key);
                      return (
                        <tr key={row.key}>
                          <td
                            colSpan={visibleColumns.length + 2}
                            style={{ background: "#e9ecef", padding: 0, borderBottom: "1px solid #dee2e6" }}
                          >
                            <div
                              onClick={() => toggle(row.key)}
                              style={{
                                position: "sticky",
                                left: 0,
                                display: "inline-block",
                                cursor: "pointer",
                                fontWeight: 700,
                                padding: "4px 8px",
                              }}
                            >
                              <i className={`bi ${isCollapsed ? "bi-caret-right-fill" : "bi-caret-down-fill"} me-1`} />
                              {row.automation}
                            </div>
                          </td>
                        </tr>
                      );
                    }
                    if (row.kind === "event") {
                      if (collapsed.has(`a:${row.automation}`)) return null;
                      const isCollapsed = collapsed.has(row.key);
                      return (
                        <tr key={row.key}>
                          <td
                            colSpan={visibleColumns.length + 2}
                            style={{ background: "#f5f6f7", padding: 0, borderBottom: "1px solid #eee" }}
                          >
                            <div
                              onClick={() => toggle(row.key)}
                              style={{
                                position: "sticky",
                                left: 0,
                                display: "inline-block",
                                cursor: "pointer",
                                fontWeight: 600,
                                padding: "3px 8px 3px 24px",
                              }}
                            >
                              <i className={`bi ${isCollapsed ? "bi-caret-right" : "bi-caret-down"} me-1`} />
                              {row.event}
                            </div>
                          </td>
                        </tr>
                      );
                    }
                    // path row (short label; automation/event come from the headers)
                    if (collapsed.has(`a:${row.path.automation}`)) return null;
                    if (collapsed.has(`e:${row.path.automation}:${row.path.event}`)) return null;
                    const p = row.path;
                    return renderPathRow(
                      p,
                      row.key,
                      <>
                        <span className="text-muted">{p.action}</span> · {p.target}
                      </>,
                      40,
                      pathAgg(p),
                    );
                  })
                : sortedRows.map(({ p, agg }, idx) =>
                    renderPathRow(
                      p,
                      `${p.automation}|${p.event}|${p.action}|${p.target}`,
                      <>
                        <span className="text-muted me-1">{idx + 1}.</span>
                        <span className="text-muted">
                          {p.automation} . {p.event} .{" "}
                        </span>
                        {p.action} . {p.target}
                      </>,
                      8,
                      agg,
                    ),
                  )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default MailTemplateMatrix;
